import React, {useState} from "react";
import {Button, Checkbox, Col, Form, Input, Popover, Row, message, Spin, Select} from "antd";
import {credentialsRequest} from "../../../../api/credentialsRequests";
import owlImg from "../../../../assets/images/myedu_owl.png";
import injectWindowSize from "../../../../hoc/injectWindowSize";
import 'antd/dist/antd.css';
import styles from '../index.module.scss';
import italyProvinces from "../../../../utils/italyProvinces";
import formItems from "./FormItems";
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {CAMPAIGN_NAME} from "../../../../environment";

const formTitle = "Compila il form e potrai accedere al corso di italiano per bambini stranieri di MyEdu";
const formFieldMissingMessage = "Il campo è obbligatorio";
const formFieldEmailErrorMessage = "Indirizzo email non valido";
const privacyLabels = ["privacy_policy", "advertising_policy", "promotions_policy"];

const MainForm =
({
  windowSize,
  showAutocompleteButton = false,
  loading,
  setLoading,
  setMobile,
  toggleModal,
  translateErrors,
  mobileVerified,
 }) => {
  const [credentialsFormErrors, setCredentialsFormErrors] = useState("");
  const [credentialsForm] = Form.useForm();
  const {executeRecaptcha} = useGoogleReCaptcha();

  // invoked by the credentials request form if there are no completion errors
  const onCredentialsOk = async (values) => {
    setLoading(true)

    // if the recaptcha fails or is null/undefined, credentials are not sent
    if (!executeRecaptcha) {
      message.error('Recaptcha non disponibile');
      setLoading(false);
      return;
    }

    // get recaptcha token
    const token = await executeRecaptcha("submit");

    // send user form data with recaptcha token and campaign_name
    try {
      await credentialsRequest({
        ...values,
        campaign_name: CAMPAIGN_NAME,
        recaptcha_token: token,
      });
      setMobile(values.mobile); // save mobile number in state to remember it for the mobile code verification
      toggleModal(); // opens verify mobile modal
      credentialsForm.resetFields();
    } catch(err) {
      try {
        const {
          data = {},
        } = err.response || {};
        const {
          message: errorMessage = null,
        } = data || {};

        // in case of single/multiple error/s
        switch (true) {
          case typeof errorMessage === 'string' || errorMessage instanceof String:
            message.error(translateErrors(errorMessage));
            break;
          case Object.keys(errorMessage).length > 0:
            setCredentialsFormErrors(errorMessage);
            break;
          default:
            message.error('Si è verififcato un errore, ti preghiamo di riprovare');
        }
      } catch (e) {
        console.error(e);
        message.error('Si è verififcato un errore, ti preghiamo di riprovare');
      }
    }

    setLoading(false);
  };

  // invoked by the credentials request form if there are completion errors
  const onCredentialsFailed = () => {
    message.error("Si è verificato un errore, ti preghiamo di riprovare")
  };

  // Get the form rules for each form field
  const getFormItemRule = item => {
    let rules;

    switch (item) {
      case "name":
      case "surname":
      case "mobile":
      case "institution":
      case "fiscal_code":
      case "province":
      case "city":
      case "privacy_policy":
      case "advertising_policy":
      case "promotions_policy":
        rules = [{required: true, message: formFieldMissingMessage}];
        break;
      case "email":
        rules = [
          {required: true, message: formFieldMissingMessage},
          {type: "email", message: formFieldEmailErrorMessage},
        ];
        break;
      case "email_verify": // email_verify field needs to be equal to email field
        rules = [
          {required: true, message: formFieldMissingMessage},
          {type: "email", message: formFieldEmailErrorMessage},
          ({getFieldValue}) => ({
            validator(_, value) {
              if (!value || getFieldValue('email') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Le due email non corrispondono'));
            },
          }),
        ];
        break;
      case "phone":
      default:
        rules = [{required: false}];
        break;
    }

    return rules;
  }

  // Get the input type for each form field
  const getFormItemInput = (item, title, link) => {
    switch (item) {
      case "name":
      case "surname":
      case "email":
      case "mobile":
      case "institution":
      case "fiscal_code":
      case "phone":
      case "city":
        return <Input placeholder={title}/>
      case "email_verify":
        return (
          <Input
            dependencies={['email']}
            // hasFeedback
            placeholder={title}
          />
        )
      case "privacy_policy":
      case "advertising_policy":
      case "promotions_policy":
        return <Checkbox>{link}{title}</Checkbox>
      case "province": // italian provinces selector
        return (
          <Select
            style={{width: '100%'}}
            showSearch
            placeholder={title}
            optionFilterProp="title"
          >
            {
              italyProvinces.map(({nome, sigla}) =>
                <Select.Option
                  value={sigla}
                  key={sigla}
                  title={nome}
                >
                  {nome}
                </Select.Option>
              )
            }
          </Select>
        );
      default:
        return <Input/>
    }
  }

  const validateProps = key => {
    const {
      [key]: fieldErrors = [],
    } = credentialsFormErrors || {};

    if (fieldErrors.length > 0) {
      return {
        validateStatus: 'error',
        help: fieldErrors.map(translateErrors).join("\n")
      }
    }

    return {};
  }

  // credentials request form
  return mobileVerified ?
    ( // if the user has verified his mobile number
      <Row
          className={styles.formDescriptionRow}
      >
        <Col
          className={styles.formTitleCol}
          xs={24}
        >
          <h2 className={styles.formSuccessTitle}>
            Registrazione avvenuta con successo!
          </h2>
        </Col>
      </Row>
    ) :
    ( // if the user has NOT verified his mobile number
      <Row
        className={styles.formDescriptionRow}
      >
        <Col
          className={styles.formTitleCol}
          xs={24}
        >
          <h2 className={styles.formTitle}>{formTitle}</h2>
          <Popover
            placement="rightTop"
            content={
              <h2
                style={{maxWidth: windowSize.width - 560}}
                className={styles.formTitlePopover}
              >
                {formTitle}
              </h2>
            }
            open={windowSize.width >= 1200}
          >
            <img
              className={styles.formTitleImg}
              alt="Gufo"
              src={owlImg}
            />
          </Popover>
        </Col>
        <Col xs={24}>
          <div className={styles.formWrapper}>
            <Form
              form={credentialsForm}
              name="basic"
              labelCol={{span: 24}}
              wrapperCol={{span: 24}}
              onFinish={onCredentialsOk}
              onFinishFailed={onCredentialsFailed}
              scrollToFirstError
            >
              {
                formItems && formItems.map(item => (
                  <Form.Item
                    key={item.label}
                    name={item.label}
                    rules={getFormItemRule(item.label)}
                    valuePropName={privacyLabels.includes(item.label) ? 'checked' : 'value'}
                    {...validateProps(item.label)}
                  >
                    {getFormItemInput(item.label, item.title, item.link)}
                  </Form.Item>
                ))
              }
              <p className={styles.formInfoText}>
                Posso in ogni momento revocare la mia dichiarazione di consenso a FME Education S.p.A. per future
                comunicazioni di marketing scrivendo all'indirizzo e-mail &nbsp;
                <a
                  href="mailto:privacy@fmeeducation.it."
                  className={styles.formLink}
                >
                  privacy@fmeeducation.it.
                </a>
                &nbsp; Attraverso il medesimo canale di comunicazione posso, inoltre,
                ottenere informazioni in merito ai miei dati archiviati
                da FME Education S.p.A. così come posso chiedere la correzione, la cancellazione, o il blocco
                dei miei dati personali. Ulteriori dettagli sul trattamento dei miei dati personali da parte
                di FME Education S.p.A. e sui miei diritti si possono trovare nella &nbsp;
                <a
                  href='https://myedufamily.it/documenti/privacy-policy'
                  className={styles.formLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Informativa Privacy
                </a>
                &nbsp; di FME Education S.p.A..
              </p>
              <Row className={styles.submitRow}>
                <Col
                  xs={24}
                  md={8}
                >
                  <Form.Item wrapperCol={{span: 24}}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      disabled={loading}
                      className={styles.formSubmitBtn}
                    >
                      {
                        loading ?
                          <Spin /> :
                          "Invia"
                      }
                    </Button>
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  md={16}
                >
                  <p className={styles.formSubmitText}>I campi contrassegnati con <b>*</b> sono obbligatori</p>
                </Col>
              </Row>
            </Form>
          </div>
        </Col>
      </Row>
    )
}

// injectWindowSize hoc adds current window size informations into wrapped component props
export default injectWindowSize(MainForm);